import type { PudoLocation } from '../../db/PudoLocation.js';
import type { ServiceNowTicket } from '../../db/ServiceNowTicket.js';
import * as m from '../../localization/out/messages.js';
import { googleMapsUrl, type MailRecipients, mailtoHref, windowLabel } from './_colleague-format.js';
import { formatReplyCode } from './_reply-code.js';
import { STATE_BADGE_CLASS } from './_state-badge.js';

// Re-exported so existing importers (the colleague page) keep working; it lives in _colleague-format.ts.
export { googleMapsUrl };

/**
 * Mapbox glue for the colleague map view.
 *
 * mapbox-gl is pulled from a CDN at first use rather than bundled: the dev container bakes
 * node_modules into its image and only mounts `src/`, so adding it as a dependency would force a
 * full (emulated, slow) image rebuild for everyone. The trade is that the map view needs public
 * internet — the rest of the app, including the colleague list, works without it, and
 * {@link loadMapbox} rejects cleanly so the caller can say so.
 */

const MAPBOX_VERSION = '3.7.0';
const MAPBOX_JS = `https://cdn.jsdelivr.net/npm/mapbox-gl@${MAPBOX_VERSION}/dist/mapbox-gl.js`;
const MAPBOX_CSS = `https://cdn.jsdelivr.net/npm/mapbox-gl@${MAPBOX_VERSION}/dist/mapbox-gl.css`;

/** Basemap. Light only — the app has no dark mode today. */
const MAP_STYLE = 'mapbox://styles/mapbox/light-v11';

/** France, roughly — where every engineer on the roster is based. Used until pins set the bounds. */
const DEFAULT_CENTER: [number, number] = [2.35, 46.6];
const DEFAULT_ZOOM = 5;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mapbox-gl has no types here (CDN global).
type MapboxGL = any;

let mapboxPromise: Promise<MapboxGL> | null = null;

/** Load mapbox-gl (script + stylesheet) once per page. Rejects if the CDN can't be reached. */
export function loadMapbox(): Promise<MapboxGL> {
  if (mapboxPromise) return mapboxPromise;

  mapboxPromise = new Promise<MapboxGL>((resolve, reject) => {
    const existing = (window as unknown as { mapboxgl?: MapboxGL }).mapboxgl;
    if (existing) {
      resolve(existing);
      return;
    }

    if (!document.querySelector(`link[href="${MAPBOX_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = MAPBOX_CSS;
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = MAPBOX_JS;
    script.async = true;
    script.onload = () => {
      const gl = (window as unknown as { mapboxgl?: MapboxGL }).mapboxgl;
      if (gl) resolve(gl);
      else reject(new Error('mapbox-gl loaded but did not register a global'));
    };
    script.onerror = () => {
      // Let a later attempt retry instead of caching the failure forever.
      mapboxPromise = null;
      reject(new Error('Could not load mapbox-gl from the CDN'));
    };
    document.head.appendChild(script);
  });

  return mapboxPromise;
}

/** Escape text before it goes into a popup's innerHTML. */
function esc(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Class put on our popups so the stylesheet below can reach them without restyling every mapbox
 * popup on the page.
 */
const POPUP_CLASS = 'colleague-popup';

/**
 * Restyle the popup shell to match the list cards.
 *
 * This has to be real CSS rather than utility classes: the parts that look wrong — the cramped
 * white box, the tip, and above all the close button, which mapbox renders as a ~13px glyph jammed
 * into the corner with no hit area — belong to mapbox's own markup, which we never author. The
 * selectors carry one class more than mapbox's own, so they win regardless of which stylesheet the
 * CDN finishes loading last.
 *
 * Colours come from the daisyUI theme variables so the popup follows the app rather than mapbox's
 * hard-coded white.
 */
const POPUP_STYLE_ID = 'colleague-popup-style';

const POPUP_CSS = `
.mapboxgl-popup.${POPUP_CLASS} .mapboxgl-popup-content {
  padding: 0.75rem;
  max-height: 20rem;
  overflow-y: auto;
  border-radius: 0.75rem;
  border: 1px solid var(--color-base-300);
  background: var(--color-base-100);
  color: var(--color-base-content);
  box-shadow: 0 10px 25px -5px rgb(0 0 0 / 0.18), 0 4px 10px -6px rgb(0 0 0 / 0.12);
  font-family: inherit;
}

/* The tip is a CSS triangle built from borders, so only the side facing the content may be
   coloured — setting them all would draw a square. Mapbox picks the side per anchor; mirror that. */
.${POPUP_CLASS}.mapboxgl-popup-anchor-top .mapboxgl-popup-tip,
.${POPUP_CLASS}.mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip,
.${POPUP_CLASS}.mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip {
  border-bottom-color: var(--color-base-100);
}
.${POPUP_CLASS}.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip,
.${POPUP_CLASS}.mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip,
.${POPUP_CLASS}.mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip {
  border-top-color: var(--color-base-100);
}
.${POPUP_CLASS}.mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
  border-right-color: var(--color-base-100);
}
.${POPUP_CLASS}.mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
  border-left-color: var(--color-base-100);
}

.mapboxgl-popup.${POPUP_CLASS} .mapboxgl-popup-close-button {
  top: 0.375rem;
  right: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border-radius: 9999px;
  font-size: 1.125rem;
  line-height: 1;
  color: color-mix(in oklab, var(--color-base-content) 55%, transparent);
  transition: background-color 120ms ease, color 120ms ease;
}
.mapboxgl-popup.${POPUP_CLASS} .mapboxgl-popup-close-button:hover,
.mapboxgl-popup.${POPUP_CLASS} .mapboxgl-popup-close-button:focus-visible {
  background: var(--color-base-200);
  color: var(--color-base-content);
}
`;

/** Add the popup stylesheet once per page. */
function ensurePopupStyles(): void {
  if (document.getElementById(POPUP_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = POPUP_STYLE_ID;
  style.textContent = POPUP_CSS;
  document.head.appendChild(style);
}

/** One `<icon> text` row, matching the list card's detail rows. */
function iconRow(icon: string, content: string): string {
  return `<div class="flex items-center gap-1">
    <span class="icon-[material-symbols--${icon}] text-sm shrink-0"></span>
    <span class="text-pretty">${content}</span>
  </div>`;
}

/**
 * The ticket popup, built to read as the same object as the list card: number chip, engineer,
 * schedule window and reply code on the right, then state, address and coordinates, then the one
 * action an FE has here — mail the service desk.
 *
 * Written as an HTML string because mapbox owns this subtree and takes `setHTML`; every
 * interpolated value therefore goes through {@link esc}. The classes are ordinary Tailwind and are
 * picked up by the `@source "**\/*.ts"` scan in `client/tailwind.css`, so the popup and the card
 * stay on one styling system.
 */
function ticketPopupHtml(ticket: ServiceNowTicket, recipients: MailRecipients | null | undefined): string {
  const mailHref = recipients ? mailtoHref(ticket, recipients) : null;
  const hasCoords = typeof ticket.locationLat === 'number' && typeof ticket.locationLng === 'number';
  const reply = formatReplyCode(ticket);
  const stateClass = STATE_BADGE_CLASS[ticket.state ?? ''] ?? 'badge-neutral';

  const details: string[] = [];
  if (ticket.stateLabel) {
    details.push(`<span class="badge badge-sm ${stateClass} self-start">${esc(ticket.stateLabel)}</span>`);
  }
  if (ticket.locationLabel) details.push(iconRow('location-on-outline', esc(ticket.locationLabel)));
  if (hasCoords) {
    const url = googleMapsUrl(ticket.locationLat!, ticket.locationLng!);
    const label = `${ticket.locationLat!.toFixed(5)}, ${ticket.locationLng!.toFixed(5)}`;
    details.push(
      iconRow(
        'my-location',
        `<a class="link link-primary" href="${esc(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`,
      ),
    );
  }
  if (ticket.clientName) details.push(iconRow('apartment', esc(ticket.clientName)));
  if (ticket.contactPhone) {
    details.push(
      iconRow('call-outline', `<a class="link" href="tel:${esc(ticket.contactPhone)}">${esc(ticket.contactPhone)}</a>`),
    );
  }

  return `
    <div class="flex flex-col gap-2 min-w-[13rem] text-base-content">
      <div class="flex items-start justify-between gap-2 pr-6">
        <div class="flex flex-col gap-1 min-w-0">
          <span class="font-mono text-xs bg-base-200 px-2 py-0.5 rounded self-start">${esc(ticket.number)}</span>
          ${ticket.assignedToName ? `<span class="text-sm font-semibold truncate">${esc(ticket.assignedToName)}</span>` : ''}
        </div>
        <div class="flex flex-col items-end gap-1 shrink-0 text-xs">
          <span class="flex items-center gap-1 text-base-content/70">
            <span class="icon-[material-symbols--schedule-outline] text-sm"></span>
            ${esc(windowLabel(ticket.windowStart, ticket.windowEnd))}
          </span>
          ${
            reply
              ? `<span class="flex items-center gap-1 font-medium ${reply.known ? 'text-info' : 'text-warning'}" title="${esc(reply.tooltip)}">
                   <span class="icon-[material-symbols--flag-outline] text-sm"></span>
                   ${esc(reply.label)}
                 </span>`
              : ''
          }
        </div>
      </div>

      ${ticket.title ? `<span class="text-sm">${esc(ticket.title)}</span>` : ''}

      ${details.length > 0 ? `<div class="flex flex-col gap-1 text-xs text-base-content/60">${details.join('')}</div>` : ''}

      ${
        mailHref
          ? `<div class="flex justify-end">
        <a class="btn btn-xs btn-outline btn-primary gap-1 inline-flex items-center leading-none" href="${esc(mailHref)}">
          <span class="icon-[material-symbols--mail-outline] text-sm shrink-0"></span>
          <span>${esc(m.colleague_email_button())}</span>
        </a>
      </div>`
          : ''
      }
    </div>
  `;
}

/**
 * The depot popup. Same shell as the ticket popup but deliberately plainer — a PUDO is a place,
 * not a job, so it carries no state, no schedule and no action.
 */
function pudoPopupHtml(pudo: PudoLocation): string {
  const address = [pudo.street, pudo.zipCode, pudo.city].filter(Boolean).map(esc).join(', ');
  const hasCoords = typeof pudo.lat === 'number' && typeof pudo.lng === 'number';

  const details: string[] = [];
  if (address) details.push(iconRow('location-on-outline', address));
  if (pudo.siteAccessHours) details.push(iconRow('schedule-outline', esc(pudo.siteAccessHours)));
  if (hasCoords) {
    const url = googleMapsUrl(pudo.lat!, pudo.lng!);
    const label = `${pudo.lat!.toFixed(5)}, ${pudo.lng!.toFixed(5)}`;
    details.push(
      iconRow(
        'my-location',
        `<a class="link link-primary" href="${esc(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`,
      ),
    );
  }

  return `
    <div class="flex flex-col gap-2 min-w-[13rem] text-base-content">
      <div class="flex flex-col gap-1 pr-6">
        <span class="badge badge-sm badge-info self-start gap-1">
          <span class="icon-[material-symbols--warehouse-outline] text-sm"></span>
          PUDO
        </span>
        <span class="text-sm font-semibold text-pretty">${esc(pudo.name)}</span>
        ${pudo.company ? `<span class="text-xs text-base-content/60">${esc(pudo.company)}</span>` : ''}
      </div>
      ${details.length > 0 ? `<div class="flex flex-col gap-1 text-xs text-base-content/60">${details.join('')}</div>` : ''}
    </div>
  `;
}

/** A ticket pin — small, primary-coloured, numbered by nothing (the popup carries the detail). */
function ticketMarkerEl(): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText =
    'width:16px;height:16px;border-radius:50%;background:#f37021;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);cursor:pointer';
  return el;
}

/** A PUDO pin — deliberately larger and a different colour, per the spec. */
function pudoMarkerEl(): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText =
    'width:26px;height:26px;border-radius:6px;background:#1d4ed8;border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.45);cursor:pointer;display:flex;align-items:center;justify-content:center;color:#fff;font-size:13px;font-weight:700';
  el.textContent = 'P';
  return el;
}

/**
 * Pan the map so a freshly opened popup is fully visible.
 *
 * The popups are card-sized now, and mapbox only flips a popup's anchor based on where the *pin*
 * sits — it never accounts for the popup's own height — so one opened on a pin in the lower half of
 * the map is clipped by the container. Nudge the camera by exactly the overflow instead, which
 * leaves the pin where the user expects it.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- CDN global, untyped.
function panPopupIntoView(map: any, popup: any): void {
  // The element has no layout until the browser has painted it once.
  requestAnimationFrame(() => {
    const el = popup.getElement() as HTMLElement | undefined;
    const container = map.getContainer() as HTMLElement | undefined;
    if (!el || !container) return;

    const popupBox = el.getBoundingClientRect();
    const mapBox = container.getBoundingClientRect();
    const margin = 12;

    // Positive panBy values move the content the other way, so an overflow on the bottom/right is
    // corrected by a positive pan and one on the top/left by a negative one.
    let dy = 0;
    if (popupBox.bottom > mapBox.bottom - margin) dy = popupBox.bottom - mapBox.bottom + margin;
    else if (popupBox.top < mapBox.top + margin) dy = popupBox.top - mapBox.top - margin;

    let dx = 0;
    if (popupBox.right > mapBox.right - margin) dx = popupBox.right - mapBox.right + margin;
    else if (popupBox.left < mapBox.left + margin) dx = popupBox.left - mapBox.left - margin;

    if (dx !== 0 || dy !== 0) map.panBy([dx, dy], { duration: 200 });
  });
}

export interface ColleagueMapHandle {
  /** Re-place every pin from the given data and refit the view. */
  update(tickets: ServiceNowTicket[], pudos: PudoLocation[], recipients: MailRecipients | null | undefined): void;
  destroy(): void;
}

/**
 * Create the map inside `container` and return a handle for feeding it data.
 *
 * The map instance outlives re-renders — only the markers are torn down and rebuilt on update,
 * which keeps the user's pan/zoom unless the ticket set itself changed.
 */
export async function createColleagueMap(container: HTMLElement, accessToken: string): Promise<ColleagueMapHandle> {
  const mapboxgl = await loadMapbox();
  mapboxgl.accessToken = accessToken;
  ensurePopupStyles();

  const map = new mapboxgl.Map({ container, style: MAP_STYLE, center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM });
  map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

  // Keep the canvas correct when the surrounding layout changes (filters wrapping, keyboard on
  // mobile) — mapbox only re-reads its container size when told to.
  const resizeObserver = new ResizeObserver(() => map.resize());
  resizeObserver.observe(container);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- CDN global, untyped.
  let markers: any[] = [];
  let lastSignature = '';

  const clearMarkers = () => {
    for (const marker of markers) marker.remove();
    markers = [];
  };

  const update = (
    tickets: ServiceNowTicket[],
    pudos: PudoLocation[],
    recipients: MailRecipients | null | undefined,
  ) => {
    clearMarkers();

    const placedTickets = tickets.filter(t => typeof t.locationLat === 'number' && typeof t.locationLng === 'number');
    const placedPudos = pudos.filter(p => typeof p.lat === 'number' && typeof p.lng === 'number');

    for (const pudo of placedPudos) {
      const popup = new mapboxgl.Popup({ offset: 18, maxWidth: '320px', className: POPUP_CLASS }).setHTML(
        pudoPopupHtml(pudo),
      );
      popup.on('open', () => panPopupIntoView(map, popup));
      const marker = new mapboxgl.Marker({ element: pudoMarkerEl() })
        .setLngLat([pudo.lng!, pudo.lat!])
        .setPopup(popup)
        .addTo(map);
      markers.push(marker);
    }

    // Tickets after PUDOs so a ticket pin is never hidden under a (bigger) depot pin.
    for (const ticket of placedTickets) {
      const popup = new mapboxgl.Popup({ offset: 12, maxWidth: '320px', className: POPUP_CLASS }).setHTML(
        ticketPopupHtml(ticket, recipients),
      );
      popup.on('open', () => panPopupIntoView(map, popup));
      const marker = new mapboxgl.Marker({ element: ticketMarkerEl() })
        .setLngLat([ticket.locationLng!, ticket.locationLat!])
        .setPopup(popup)
        .addTo(map);
      markers.push(marker);
    }

    // Only refit when the ticket set actually changed, so toggling back from the list (or a PUDO
    // refresh) doesn't yank the user out of the area they had panned to.
    const signature = placedTickets.map(t => t.number).join('|');
    if (signature && signature !== lastSignature) {
      lastSignature = signature;
      const bounds = new mapboxgl.LngLatBounds();
      for (const t of placedTickets) bounds.extend([t.locationLng!, t.locationLat!]);
      map.fitBounds(bounds, { padding: 48, maxZoom: 13, duration: 0 });
    }
  };

  return {
    update,
    destroy() {
      resizeObserver.disconnect();
      clearMarkers();
      map.remove();
    },
  };
}
